import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MessageSquare } from "lucide-react";
import { Badge } from "../components/ui/badge";
import Compose from "./communication/Compose";
import History from "./communication/History";
import Inbox from "./communication/Inbox";
import Templates, { TemplateDialog } from "./communication/Templates";

export default function Communication() {
  const { user } = useAuth();
  const canWrite = ["admin", "manager"].includes(user?.role);
  const [sp] = useSearchParams();
  const [tab, setTab] = useState("compose");
  const [refs, setRefs] = useState({ properties: [], tenants: [], templates: [], maintenance: [] });
  const [stats, setStats] = useState(null);
  const [tplEdit, setTplEdit] = useState(null);

  const loadRefs = useCallback(() => {
    Promise.all([
      api.get("/properties").catch(() => ({ data: [] })),
      api.get("/tenants").catch(() => ({ data: [] })),
      api.get("/message-templates").catch(() => ({ data: [] })),
      api.get("/maintenance").catch(() => ({ data: [] })),
    ]).then(([p, t, tpl, m]) => setRefs({ properties: p.data, tenants: t.data, templates: tpl.data, maintenance: m.data }));
    api.get("/messaging/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { if (sp.get("tab")) setTab(sp.get("tab")); }, [sp]);

  return (
    <div className="space-y-6" data-testid="page-communication">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Tenant Relations</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><MessageSquare className="w-7 h-7 text-primary" /> Communication Center</h1>
          <p className="text-sm text-muted-foreground mt-1">Send email & SMS announcements, notices and appointments — with templates, confirmations and full history.</p>
        </div>
        {stats && !stats.sms_configured && <Badge className="rounded-lg bg-amber-100 text-amber-800 dark:bg-amber-900/40">SMS not configured</Badge>}
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="messaging-stats">
          {[["awaiting_confirmation", "Awaiting Confirmation", "text-amber-600"], ["no_response", "No Response", "text-orange-600"], ["follow_up", "Follow-up Needed", "text-blue-600"], ["failed", "Failed Deliveries", "text-red-600"], ["scheduled", "Scheduled", "text-violet-600"]].map(([k, l, tone]) => (
            <div key={k} className="rounded-2xl border border-border bg-card p-4"><p className={`font-heading text-2xl font-bold ${tone}`}>{stats[k]}</p><p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mt-1">{l}</p></div>
          ))}
        </div>
      )}

      <div className="flex gap-2 border-b border-border">
        {[["compose", "Compose"], ["inbox", "Inbox"], ["history", "History"], ["templates", "Templates"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "compose" && (canWrite ? <Compose refs={refs} onSent={loadRefs} /> : <p className="text-sm text-muted-foreground">You have read-only access. Viewing message history is available under the History tab.</p>)}
      {tab === "inbox" && <Inbox canWrite={canWrite} />}
      {tab === "history" && <History refs={refs} canWrite={canWrite} />}
      {tab === "templates" && <Templates refs={refs} canWrite={canWrite} onEdit={setTplEdit} reload={loadRefs} />}

      {tplEdit !== null && <TemplateDialog tpl={tplEdit} onClose={() => setTplEdit(null)} onSaved={() => { setTplEdit(null); loadRefs(); }} />}
    </div>
  );
}
